import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Alert, Button, Form, Modal, Badge, InputGroup, Row, Col, 
  ProgressBar, Spinner, Dropdown, Card, ListGroup, ButtonGroup,
  Breadcrumb,
} from "react-bootstrap";
import { 
  FaFileInvoice, FaFileAlt, FaTrashAlt, FaSearch, FaArrowLeft, 
  FaUpload, FaComments, FaFileDownload, FaFolder, FaEdit, FaArrowRight, FaHome,
  FaEllipsisV, FaFolderPlus
} from "react-icons/fa";
import { motion } from "framer-motion";
import EmployeeSidebar from "../../components/Sidebar";

const EmployeeDocuments = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [documentFile, setDocumentFile] = useState(null);
    const [invoiceFile, setInvoiceFile] = useState(null);
    const [userInvoices, setUserInvoices] = useState([]);
    const [userDocuments, setUserDocuments] = useState([]);
    const [chatDocuments, setChatDocuments] = useState([]);
    const [folders, setFolders] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [folderPath, setFolderPath] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState({ type: "", id: "" });
    const [itemToMove, setItemToMove] = useState({ type: "", id: "", name: "" });
    const [itemToRename, setItemToRename] = useState({ type: "", id: "", name: "" });
    const [activeTab, setActiveTab] = useState("documents");
    const [searchTerm, setSearchTerm] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [newFolderName, setNewFolderName] = useState("");
    const [newItemName, setNewItemName] = useState("");
    const [targetFolder, setTargetFolder] = useState("");
    const [employee, setEmployee] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    useEffect(() => {
        const fetchUserDocuments = async () => {
            const token = localStorage.getItem("employeeToken");
            if (!token) {
                navigate("/login");
                return;
            }

            try {
                // Fetch employee data
                const empResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!empResponse.ok) throw new Error("Failed to fetch employee data");
                const empData = await empResponse.json();
                setEmployee(empData.employee);

                // Fetch client data
                const client = empData.employee.clients.find(c => c._id === userId);
                if (!client) throw new Error("Client not found");
                setUser(client);

                // Fetch user documents and folders
                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}`,
                    {
                    headers: { Authorization: `Bearer ${token}` },
                    }
                );

                if (!response.ok) throw new Error("Failed to fetch user documents");

                const data = await response.json();
                setUserInvoices(data.user.invoices || []);
                setUserDocuments(data.user.documents || []);
                setFolders(data.user.folders || []);

                // Fetch chat documents
                const chatDocResponse = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/chat/documents?userId=${userId}`
                );
                if (!chatDocResponse.ok) throw new Error("Failed to fetch chat documents");
                const chatDocData = await chatDocResponse.json();
                setChatDocuments(chatDocData);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUserDocuments();
        const interval = setInterval(fetchUserDocuments, 30000);
        return () => clearInterval(interval);
    }, [userId, navigate]);

    const handleFileChange = (e, type) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const fileList = Array.from(files).map(file => ({
            name: file.name,
            size: file.size,
            type: file.type,
            file
        }));

        setSelectedFiles(fileList);
        
        if (type === 'invoice') {
            setInvoiceFile(files);
        } else {
            setDocumentFile(files);
        }
    };

    const handleInvoiceSubmit = async () => {
        if (!invoiceFile) {
            alert("Please select a file");
            return;
        }

        const token = localStorage.getItem("employeeToken");
        const formData = new FormData();

        setIsUploading(true);
        setUploadProgress(0);

        if (invoiceFile instanceof FileList) {
            Array.from(invoiceFile).forEach((file) => {
                formData.append('file', file);
            });
        }
        formData.append("userId", userId);
        if (currentFolder) {
            formData.append("folderId", currentFolder);
        }

        try {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete);
                }
            });

            xhr.open("POST", `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-invoice`);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.onload = () => {
                if (xhr.status === 200) {
                    const data = JSON.parse(xhr.response);
                    setUserInvoices(data.user.invoices || []);
                    setFolders(data.user.folders || []);
                    setInvoiceFile(null);
                    setSelectedFiles([]);
                } else {
                    throw new Error("Failed to send invoice");
                }
                setIsUploading(false);
                setUploadProgress(0);
            };

            xhr.onerror = () => {
                setIsUploading(false);
                setUploadProgress(0);
                throw new Error("Network error");
            };

            xhr.send(formData);
        } catch (error) {
            setIsUploading(false);
            setUploadProgress(0);
            setError(error.message);
        }
    };

    const handleDocumentSubmit = async () => {
        if (!documentFile) {
            alert("Please select a file");
            return;
        }

        const token = localStorage.getItem("employeeToken");
        const formData = new FormData();

        setIsUploading(true);
        setUploadProgress(0);
        
        if (documentFile instanceof FileList) {
            Array.from(documentFile).forEach((file) => {
                formData.append('file', file);
            });
        }
        formData.append("userId", userId);
        if (currentFolder) {
            formData.append("folderId", currentFolder);
        }

        try {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(percentComplete);
                }
            });

            xhr.open("POST", `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-document`);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.onload = () => {
                if (xhr.status === 200) {
                    const data = JSON.parse(xhr.response);
                    setUserDocuments(data.user.documents || []);
                    setFolders(data.user.folders || []);
                    setDocumentFile(null);
                    setSelectedFiles([]);
                } else {
                    throw new Error("Failed to send document");
                }
                setIsUploading(false);
                setUploadProgress(0);
            };

            xhr.onerror = () => {
                setIsUploading(false);
                setUploadProgress(0);
                throw new Error("Network error");
            };

            xhr.send(formData);
        } catch (error) {
            setIsUploading(false);
            setUploadProgress(0);
            setError(error.message);
        }
    };

    const createFolder = async () => {
        if (!newFolderName.trim()) {
            alert("Please enter a folder name");
            return;
        }

        const token = localStorage.getItem("employeeToken");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/create-folder`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userId,
                        folderName: newFolderName,
                        parentFolderId: currentFolder
                    }),
                }
            );

            if (!response.ok) throw new Error("Failed to create folder");

            const data = await response.json();
            setFolders(data.user.folders || []);
            setNewFolderName("");
            setShowCreateFolder(false);
        } catch (error) {
            setError(error.message);
        }
    };

    const moveItem = async () => {
        if (!itemToMove.id || !targetFolder) return;

        const token = localStorage.getItem("employeeToken");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/move-item`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userId,
                        itemType: itemToMove.type,
                        itemId: itemToMove.id,
                        targetFolderId: targetFolder
                    }),
                }
            );

            if (!response.ok) throw new Error("Failed to move item");

            const data = await response.json();
            if (itemToMove.type === "invoice") {
                setUserInvoices(data.user.invoices || []);
            } else {
            setUserDocuments(data.user.documents || []);
            }
            setFolders(data.user.folders || []);
            setShowMoveModal(false);
            setItemToMove({ type: "", id: "", name: "" });
            setTargetFolder("");
        } catch (error) {
            setError(error.message);
        }
    };

    const renameItem = async () => {
        if (!itemToRename.id || !newItemName.trim()) return;

        const token = localStorage.getItem("employeeToken");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/auth/rename-item`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        userId,
                        itemType: itemToRename.type,
                        itemId: itemToRename.id,
                        newName: newItemName
                    }),
                }
            );

            if (!response.ok) throw new Error("Failed to rename item");

            const data = await response.json();
            if (itemToRename.type === "invoice") {
                setUserInvoices(data.user.invoices || []);
            } else if (itemToRename.type === "document") {
                setUserDocuments(data.user.documents || []);
            } else {
                setFolders(data.user.folders || []);
            }
            setShowRenameModal(false);
            setItemToRename({ type: "", id: "", name: "" });
            setNewItemName("");
        } catch (error) {
            setError(error.message);
        }
    };

    const deleteItem = async () => {
        const token = localStorage.getItem("employeeToken");
        try {
            let response;
            if (itemToDelete.type === "invoice") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/invoice/${itemToDelete.id}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } else if (itemToDelete.type === "document") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/document/${itemToDelete.id}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } else if (itemToDelete.type === "folder") {
                response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/folder/${itemToDelete.id}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            }

            if (!response.ok) throw new Error("Failed to delete item");

            const data = await response.json();
            if (itemToDelete.type === "invoice") {
                setUserInvoices(data.user.invoices || []);
            } else if (itemToDelete.type === "document") {
            setUserDocuments(data.user.documents || []);
            } else {
                setFolders(data.user.folders || []);
            }
            setShowDeleteConfirm(false);
        } catch (error) {
            setError(error.message);
        }
    };

    const openFolder = (folder) => {
        setCurrentFolder(folder._id);
        setFolderPath([...folderPath, folder]);
    };

    const navigateToFolder = (index) => {
        if (index === -1) {
            setCurrentFolder(null);
            setFolderPath([]);
        } else {
            const newPath = folderPath.slice(0, index + 1);
            setFolderPath(newPath);
            setCurrentFolder(newPath[newPath.length - 1]._id);
        }
    };

    const getCurrentFolderContents = () => {
        if (!currentFolder) return folders.filter(f => !f.parentFolder);
        return folders.filter(f => f.parentFolder === currentFolder);
    };

    const getCurrentFolderDocuments = () => {
        return userDocuments.filter(doc => doc.folderId === currentFolder);
    };

    const getCurrentFolderInvoices = () => {
        return userInvoices.filter(inv => inv.folderId === currentFolder);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getAllFolders = () => {
        return folders.filter(f => f._id !== currentFolder);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5">
                <Alert variant="danger" className="text-center">
                    {error}
                    <Button variant="link" onClick={() => window.location.reload()}>
                        Try Again
                    </Button>
                </Alert>
            </div>
        );
    }

    return (
        <div className="admin-dashboard-container">
            <EmployeeSidebar
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                employee={employee}
            />

            <div className="main-content-container">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                <Button 
                    variant="outline-primary" 
                    onClick={() => navigate(-1)} 
                    className="mb-4 d-flex align-items-center"
                >
                    <FaArrowLeft className="me-2" />
                    Back to Dashboard
                </Button>

                <Row className="align-items-center mb-4">
                    <Col md={6} className="mb-3 mb-md-0">
                        <h2 className="mb-0 text-center text-md-start">
                            Documents for <span className="text-primary">{user?.name}</span>
                        </h2>
                    </Col>
                    <Col md={6} className="d-flex justify-content-end">
                        <InputGroup style={{ maxWidth: '500px' }}>
                            <InputGroup.Text>
                                <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                    placeholder="Search files and folders..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="rounded-end"
                            />
                        </InputGroup>
                    </Col>
                </Row>

                    {/* Breadcrumb Navigation */}
                    <Breadcrumb className="mb-4">
                        <Breadcrumb.Item 
                            active={!currentFolder}
                            onClick={() => navigateToFolder(-1)}
                            style={{ cursor: 'pointer' }}
                        >
                            <FaHome className="me-1" />
                            Root
                        </Breadcrumb.Item>
                        {folderPath.map((folder, index) => (
                            <Breadcrumb.Item 
                                key={folder._id}
                                active={index === folderPath.length - 1}
                                onClick={() => navigateToFolder(index)}
                                style={{ cursor: 'pointer' }}
                            >
                                <FaFolder className="me-1" />
                                {folder.name}
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>

                    {/* Action Buttons */}
                <div className="mb-4">
                        <ButtonGroup className="me-2">
                            <Button
                                variant="success"
                                onClick={() => setShowCreateFolder(true)}
                            >
                                <FaFolderPlus className="me-2" />
                                New Folder
                            </Button>
                        </ButtonGroup>
                        
                        <ButtonGroup>
                    <Button
                                variant={activeTab === "documents" ? "secondary" : "outline-secondary"}
                        onClick={() => setActiveTab("documents")}
                    >
                        <FaFileAlt className="me-2" />
                        Documents
                    </Button>
                            <Button
                                variant={activeTab === "invoices" ? "primary" : "outline-primary"}
                                onClick={() => setActiveTab("invoices")}
                            >
                                <FaFileInvoice className="me-2" />
                                Invoices
                            </Button>
                    <Button
                        variant={activeTab === "chat-documents" ? "info" : "outline-info"}
                        onClick={() => setActiveTab("chat-documents")}
                    >
                        <FaComments className="me-2" />
                        Chat Documents
                    </Button>
                        </ButtonGroup>
                </div>

                    {/* Folders Section */}
                    {getCurrentFolderContents().length > 0 && (
                        <Card className="mb-4">
                            <Card.Header>
                                <h6 className="mb-0">
                                    <FaFolder className="me-2" />
                                    Folders
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {getCurrentFolderContents().map((folder) => (
                                        <Col key={folder._id} md={3} className="mb-3">
                                            <Card className="h-100 folder-card" style={{ cursor: 'pointer' }}>
                                                <Card.Body className="d-flex flex-column align-items-center p-3">
                                                    <div className="position-relative">
                                                        <FaFolder 
                                                            size={48} 
                                                            className="text-warning mb-2"
                                                            onClick={() => openFolder(folder)}
                                                        />
                                                        <Dropdown className="position-absolute top-0 end-0">
                                                            <Dropdown.Toggle 
                                                                variant="link" 
                                                                size="sm"
                                                                className="p-0 border-0"
                                                            >
                                                                <FaEllipsisV />
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item 
                                                                    onClick={() => {
                                                                        setItemToRename({ 
                                                                            type: "folder", 
                                                                            id: folder._id, 
                                                                            name: folder.name 
                                                                        });
                                                                        setNewItemName(folder.name);
                                                                        setShowRenameModal(true);
                                                                    }}
                                                                >
                                                                    <FaEdit className="me-2" />
                                                                    Rename
                                                                </Dropdown.Item>
                                                                <Dropdown.Item 
                                                                    onClick={() => {
                                                                        setItemToDelete({ type: "folder", id: folder._id });
                                                                        setShowDeleteConfirm(true);
                                                                    }}
                                                                    className="text-danger"
                                                                >
                                                                    <FaTrashAlt className="me-2" />
                                                                    Delete
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                    <span 
                                                        className="text-center small"
                                                        onClick={() => openFolder(folder)}
                                                    >
                                                        {folder.name}
                                                    </span>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Documents Section */}
                {activeTab === "documents" && (
                    <>
                        {/* Upload Document Card */}
                            <Card className="mb-4">
                                <Card.Header>
                                    <h6 className="mb-0">
                                        <FaUpload className="me-2" />
                                        Upload Documents
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Control
                                        type="file"
                                            onChange={(e) => handleFileChange(e, 'document')}
                                        multiple
                                            disabled={isUploading}
                                    />
                                </Form.Group>

                                    {selectedFiles.length > 0 && (
                                        <div className="mb-3">
                                            <h6>Selected Files:</h6>
                                            <ListGroup>
                                                {selectedFiles.map((file, index) => (
                                                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <FaFileAlt className="me-2 text-secondary" />
                                                            {file.name}
                                                        </div>
                                                        <Badge bg="secondary">{formatFileSize(file.size)}</Badge>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        </div>
                                    )}

                                    {isUploading && (
                                        <div className="mb-3">
                                            <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated />
                                        </div>
                                    )}

                                <Button
                                        variant="secondary"
                                    onClick={handleDocumentSubmit}
                                        disabled={!documentFile || isUploading}
                                    >
                                        {isUploading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                    <FaUpload className="me-2" />
                                                Upload Documents
                                            </>
                                        )}
                                </Button>
                                </Card.Body>
                            </Card>

                            {/* Document List */}
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">
                                        <FaFileAlt className="me-2" />
                                        Documents
                                    </h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {getCurrentFolderDocuments().length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Size</th>
                                                        <th>Date</th>
                                                        <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                    {getCurrentFolderDocuments().map((document, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                                <FaFileAlt className="me-2 text-secondary" />
                                                                {document.originalName}
                                                            </td>
                                                            <td>{formatFileSize(document.size || 0)}</td>
                                                            <td>{new Date(document.date).toLocaleDateString()}</td>
                                                            <td>
                                                                <ButtonGroup size="sm">
                                                                    <Button
                                                                        variant="outline-primary"
                                                                href={`${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`}
                                                                target="_blank"
                                                                    >
                                                                        <FaFileDownload />
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        onClick={() => {
                                                                            setItemToMove({ 
                                                                                type: "document", 
                                                                                id: document._id, 
                                                                                name: document.originalName 
                                                                            });
                                                                            setShowMoveModal(true);
                                                                        }}
                                                                    >
                                                                        <FaArrowRight />
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        onClick={() => {
                                                                            setItemToDelete({ type: "document", id: document._id });
                                                                            setShowDeleteConfirm(true);
                                                                        }}
                                                                    >
                                                                        <FaTrashAlt />
                                                                    </Button>
                                                                </ButtonGroup>
                                                        </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <FaFileAlt className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                                            <p className="text-muted">No documents in this folder</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </>
                    )}

                    {/* Invoices Section */}
                    {activeTab === "invoices" && (
                        <>
                            {/* Upload Invoice Card */}
                            <Card className="mb-4">
                                <Card.Header>
                                    <h6 className="mb-0">
                                        <FaUpload className="me-2" />
                                        Upload Invoices
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            type="file"
                                            onChange={(e) => handleFileChange(e, 'invoice')}
                                            multiple
                                            disabled={isUploading}
                                        />
                                    </Form.Group>

                                    {selectedFiles.length > 0 && (
                                        <div className="mb-3">
                                            <h6>Selected Files:</h6>
                                            <ListGroup>
                                                {selectedFiles.map((file, index) => (
                                                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                                        <div>
                                                            <FaFileInvoice className="me-2 text-primary" />
                                                            {file.name}
                                                        </div>
                                                        <Badge bg="primary">{formatFileSize(file.size)}</Badge>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        </div>
                                    )}

                                    {isUploading && (
                                        <div className="mb-3">
                                            <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated />
                                        </div>
                                    )}

                                    <Button
                                        variant="primary"
                                        onClick={handleInvoiceSubmit}
                                        disabled={!invoiceFile || isUploading}
                                    >
                                        {isUploading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <FaUpload className="me-2" />
                                                Upload Invoices
                                            </>
                                        )}
                                    </Button>
                                </Card.Body>
                            </Card>

                            {/* Invoice List */}
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">
                                        <FaFileInvoice className="me-2" />
                                        Invoices
                                    </h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {getCurrentFolderInvoices().length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Size</th>
                                                        <th>Date</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getCurrentFolderInvoices().map((invoice, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <FaFileInvoice className="me-2 text-primary" />
                                                                {invoice.originalName}
                                                        </td>
                                                            <td>{formatFileSize(invoice.size || 0)}</td>
                                                            <td>{new Date(invoice.date).toLocaleDateString()}</td>
                                                            <td>
                                                                <ButtonGroup size="sm">
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        href={`${process.env.REACT_APP_API_BASE_URL}${invoice.fileUrl}`}
                                                                        target="_blank"
                                                                    >
                                                                        <FaFileDownload />
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        onClick={() => {
                                                                            setItemToMove({ 
                                                                                type: "invoice", 
                                                                                id: invoice._id, 
                                                                                name: invoice.originalName 
                                                                            });
                                                                            setShowMoveModal(true);
                                                                        }}
                                                                    >
                                                                        <FaArrowRight />
                                                                    </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                onClick={() => {
                                                                            setItemToDelete({ type: "invoice", id: invoice._id });
                                                                    setShowDeleteConfirm(true);
                                                                }}
                                                            >
                                                                        <FaTrashAlt />
                                                            </Button>
                                                                </ButtonGroup>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                            <FaFileInvoice className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                                            <p className="text-muted">No invoices in this folder</p>
                                    </div>
                                )}
                                </Card.Body>
                            </Card>
                    </>
                )}

                    {/* Chat Documents Section */}
                {activeTab === "chat-documents" && (
                        <Card>
                            <Card.Header>
                                <h6 className="mb-0">
                                    <FaComments className="me-2" />
                                    Chat Documents
                                </h6>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {chatDocuments.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Size</th>
                                                    <th>Date</th>
                                                    <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                                {chatDocuments.map((document, index) => (
                                                <tr key={index}>
                                                    <td>
                                                            <FaFileAlt className="me-2 text-info" />
                                                            {document.originalName}
                                                        </td>
                                                        <td>{formatFileSize(document.size || 0)}</td>
                                                        <td>{new Date(document.timestamp).toLocaleDateString()}</td>
                                                        <td>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                           href={`${process.env.REACT_APP_API_BASE_URL}/uploads/chat/${document.fileUrl.split('/').pop()}`}
                                                            target="_blank"
                                                            >
                                                                <FaFileDownload />
                                                            </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                        <FaComments className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                                        <p className="text-muted">No chat documents available</p>
                                </div>
                            )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Create Folder Modal */}
                    <Modal show={showCreateFolder} onHide={() => setShowCreateFolder(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>Create New Folder</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group>
                                <Form.Label>Folder Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    placeholder="Enter folder name"
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowCreateFolder(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={createFolder}>
                                Create Folder
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Move Item Modal */}
                    <Modal show={showMoveModal} onHide={() => setShowMoveModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>Move Item</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Move <strong>{itemToMove.name}</strong> to:</p>
                            <Form.Group>
                                <Form.Label>Select Destination Folder</Form.Label>
                                <Form.Select
                                    value={targetFolder}
                                    onChange={(e) => setTargetFolder(e.target.value)}
                                >
                                    <option value="">Root Folder</option>
                                    {getAllFolders().map(folder => (
                                        <option key={folder._id} value={folder._id}>
                                            {folder.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowMoveModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={moveItem}>
                                Move
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Rename Modal */}
                    <Modal show={showRenameModal} onHide={() => setShowRenameModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>Rename Item</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group>
                                <Form.Label>New Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newItemName}
                                    onChange={(e) => setNewItemName(e.target.value)}
                                    placeholder="Enter new name"
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowRenameModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={renameItem}>
                                Rename
                            </Button>
                        </Modal.Footer>
                    </Modal>

                {/* Delete Confirmation Modal */}
                    <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
                        <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                            Are you sure you want to delete this {itemToDelete.type}? This action cannot be undone.
                    </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                            Cancel
                        </Button>
                            <Button variant="danger" onClick={deleteItem}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
                </motion.div>
            </div>
        </div>
    );
};

export default EmployeeDocuments;